import {hideableValueCreateHidden} from '@wix/devzai-utils-common';
import type {HideableValue} from '@wix/devzai-utils-common';
import {
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata,
} from '../../client-server-common';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata,
} from '../../client-server-common/doppe-action-types/doppe-action-type';

/**
 * `doppeActionTypeDownloadFile` — top-level action that renders a single CTA button
 * which downloads a Wix Media file on click, optionally gated behind a Wix Checkout
 * payment.
 *
 * Distinct from `DoppeActionCTAType.DownloadFile`: the CTA option lives inside a
 * CTA Button action's settings; this action type IS the button. Both share the same
 * settings shape (`fileId`, `fileName`, `paymentSettings`) so the existing server
 * download primitives (`generateFreeDownloadFileUrl`, `generatePaidDownloadFileUrl`)
 * and Buy/Claim modals are reused.
 *
 * Click flow (free): server mints a signed URL → browser navigates / opens in new tab.
 * Click flow (paid): Buy/Claim modal → Wix Checkout → post-checkout Claim Download
 * modal → server validates the order and mints the signed URL.
 */
export namespace DoppeActionTypeDownloadFile {

    export interface ActionSettings {
        /** Wix Media file id (Media Manager) of the private file to be downloaded. */
        fileId: string;
        /** Display name of the file (e.g. the original filename), shown in the editor. */
        fileName: string;
        /**
         * Optional payment gate. When hidden the click downloads the file
         * immediately via a server-minted signed URL. When visible the click
         * opens a Wix Checkout for the configured custom product; on return,
         * the viewer shows a claim-download modal that validates the order
         * and mints a signed URL.
         */
        paymentSettings: HideableValue<PaymentSettings>;
    }

    export interface PaymentSettings {
        productName: string;
        price: DoppeActionPrice;
        /**
         * Quantity for the custom line item. Almost always 1 — the field
         * exists only to keep parity with `WixEcomCheckout.CustomProduct`
         * (Wix Checkout requires a positive integer).
         */
        quantity: number;
        /**
         * Optional human-facing description of what's being purchased, mapped
         * to the Wix Custom line item's `itemTypeDescription` and shown as the
         * item-type label in the Wix Checkout order summary. When empty, the
         * viewer falls back to a localized "Digital download" label.
         *
         * The custom line item is always created with `kind: Custom` (never
         * `DigitalProduct`) because metasites with Wix Stores installed reject
         * DIGITAL-preset custom line items with `MISSING_DIGITAL_FILE` — only
         * Wix Stores catalog products carry the required `digitalFile.fileId`.
         */
        customItemTypeDescription: HideableValue<string>;
    }
}

export const doppeActionTypeDownloadFile = doppeActionTypeDefineMetadata<DoppeActionTypeDownloadFile.ActionSettings>({
    id: 'io.bymo.action/download-file',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        fileId: '',
        fileName: '',
        paymentSettings: hideableValueCreateHidden({
            productName: '',
            price: {
                actualPrice: 5,
                currencyCode: 'USD',
            },
            quantity: 1,
            customItemTypeDescription: hideableValueCreateHidden(''),
        }),
    }),
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            fileId: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            fileName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'downloadFile.fileName'}),
            paymentSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false,
        hideableContentSettingsNames: {
            paymentSettings: true,
        },
        bindableContentSettingsNames: {},
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: false,
    hasNoLockInnerCTA: true,
});
